import { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Navbar from '../components/Navbar'
import ItemCard from '../components/ItemCard'
import ItemCardSkeleton from '../components/ItemCardSkeleton'
import SearchBar from '../components/SearchBar'
import FilterSidebar, { type FilterState } from '../components/FilterSidebar'
import { itemsApi } from '../lib/api'
import { useAuth } from '../context/AuthContext'

interface Item {
  id: string
  title: string
  dailyPrice: number | null
  weeklyPrice: number | null
  monthlyPrice: number | null
  salePrice: number | null
  listingType: 'RENT' | 'SALE' | 'RENT_OR_SALE'
  condition: 'LIKE_NEW' | 'GOOD' | 'FAIR'
  addressApprox: string
  category: string
  photos: Array<{ id: string; url: string }>
  owner: {
    id: string
    firstName: string
    lastName: string
    verificationStatus: string
  }
  _count?: {
    rentals: number
  }
}

interface ItemsMeta {
  total: number
  page: number
  limit: number
  totalPages: number
}

interface FetchError {
  message: string
  canRetry: boolean
}

const DEFAULT_LIMIT = 12

const conditionLabelMap: Record<string, string> = {
  LIKE_NEW: 'Like New',
  GOOD: 'Good',
  FAIR: 'Fair',
}

const listingTypeLabelMap: Record<string, string> = {
  RENT: 'Rent Only',
  SALE: 'Sale Only',
  RENT_OR_SALE: 'Rent or Sale',
}

function buildSearchParams(
  params: Record<string, string | string[] | undefined | null>
): URLSearchParams {
  const next = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v) next.append(key, v)
      })
    } else {
      next.set(key, value)
    }
  })
  return next
}

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showFilters, setShowFilters] = useState(false)
  const [items, setItems] = useState<Item[]>([])
  const [meta, setMeta] = useState<ItemsMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<FetchError | null>(null)
  const { user } = useAuth()

  const searchQuery = searchParams.get('search') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)

  const filters: FilterState = useMemo(
    () => ({
      categories: searchParams.getAll('category'),
      listingTypes: searchParams.getAll('listingType'),
      conditions: searchParams.getAll('condition'),
      priceRange: {
        min: Number(searchParams.get('minPrice') || 0),
        max: Number(searchParams.get('maxPrice') || 0),
      },
    }),
    [searchParams]
  )

  const paramsKey = searchParams.toString()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const fetchItems = async () => {
      try {
        const params: Record<string, string | string[]> = {
          page: String(page),
          limit: String(DEFAULT_LIMIT),
        }

        if (searchQuery.trim()) {
          params.search = searchQuery.trim()
        }
        if (filters.categories.length) {
          params.category = filters.categories
        }
        if (filters.listingTypes.length) {
          params.listingType = filters.listingTypes
        }
        if (filters.conditions.length) {
          params.condition = filters.conditions
        }
        if (filters.priceRange.min > 0) {
          params.minPrice = String(filters.priceRange.min)
        }
        if (filters.priceRange.max > 0) {
          params.maxPrice = String(filters.priceRange.max)
        }

        const response = await itemsApi.getAll(params)
        const data = response.data

        if (cancelled) return

        if (!data.success) {
          setError({ message: data.error || 'Failed to load items', canRetry: true })
          setItems([])
          setMeta(null)
          return
        }

        const parsedItems: Item[] = (data.data || []).map((item: any) => ({
          ...item,
          dailyPrice: item.dailyPrice ? parseFloat(item.dailyPrice) : null,
          weeklyPrice: item.weeklyPrice ? parseFloat(item.weeklyPrice) : null,
          monthlyPrice: item.monthlyPrice ? parseFloat(item.monthlyPrice) : null,
          salePrice: item.salePrice ? parseFloat(item.salePrice) : null,
        }))

        setItems(parsedItems)
        setMeta(data.meta || null)
      } catch (err: any) {
        if (cancelled) return
        console.error('Error fetching items:', err)
        const status = err?.response?.status
        const backendMessage = err?.response?.data?.error
        let message = 'We’re having trouble loading items right now. Please try again in a moment.'

        if (backendMessage) {
          message = backendMessage
        } else if (status === 404) {
          message = 'Items service not found. Please try again later.'
        } else if (status && status >= 500) {
          message = 'Our servers are having a moment. Please try again.'
        }

        setError({ message, canRetry: true })
        setItems([])
        setMeta(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchItems()
    return () => {
      cancelled = true
    }
  }, [paramsKey])

  const setUrl = (updates: Record<string, string | string[] | undefined | null>) => {
    const current: Record<string, string | string[] | undefined | null> = {
      search: searchQuery || null,
      page: String(page),
      category: filters.categories,
      listingType: filters.listingTypes,
      condition: filters.conditions,
      minPrice: filters.priceRange.min > 0 ? String(filters.priceRange.min) : null,
      maxPrice: filters.priceRange.max > 0 ? String(filters.priceRange.max) : null,
    }

    const merged = { ...current, ...updates, page: updates.page ?? current.page }
    if (merged.page === '1') {
      delete merged.page
    }

    setSearchParams(buildSearchParams(merged), { replace: true })
  }

  const handleSearch = (query: string) => {
    setUrl({ search: query || null, page: '1' })
  }

  const handleFiltersChange = (newFilters: FilterState) => {
    setUrl({
      category: newFilters.categories,
      listingType: newFilters.listingTypes,
      condition: newFilters.conditions,
      minPrice: newFilters.priceRange.min > 0 ? String(newFilters.priceRange.min) : null,
      maxPrice: newFilters.priceRange.max > 0 ? String(newFilters.priceRange.max) : null,
      page: '1',
    })
  }

  const clearFilters = () => {
    setUrl({
      category: [],
      listingType: [],
      condition: [],
      minPrice: null,
      maxPrice: null,
      search: null,
      page: '1',
    })
  }

  const goToPage = (newPage: number) => {
    if (!meta || newPage < 1 || newPage > meta.totalPages) return
    setUrl({ page: String(newPage) })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const hasActiveFilters =
    filters.categories.length > 0 ||
    filters.listingTypes.length > 0 ||
    filters.conditions.length > 0 ||
    filters.priceRange.min > 0 ||
    filters.priceRange.max > 0

  const activeFilterChips: Array<{ key: string; label: string; onRemove: () => void }> = [
    ...filters.categories.map((cat) => ({
      key: `cat-${cat}`,
      label: cat,
      onRemove: () => handleFiltersChange({ ...filters, categories: filters.categories.filter((c) => c !== cat) }),
    })),
    ...filters.listingTypes.map((type) => ({
      key: `type-${type}`,
      label: listingTypeLabelMap[type] || type,
      onRemove: () => handleFiltersChange({ ...filters, listingTypes: filters.listingTypes.filter((t) => t !== type) }),
    })),
    ...filters.conditions.map((cond) => ({
      key: `cond-${cond}`,
      label: conditionLabelMap[cond] || cond,
      onRemove: () => handleFiltersChange({ ...filters, conditions: filters.conditions.filter((c) => c !== cond) }),
    })),
  ]

  if (filters.priceRange.min > 0 || filters.priceRange.max > 0) {
    const min = filters.priceRange.min || 0
    const max = filters.priceRange.max > 0 ? filters.priceRange.max : ''
    activeFilterChips.push({
      key: 'price',
      label: `Price: $${min}${max ? ` – $${max}` : '+'}`,
      onRemove: () => handleFiltersChange({ ...filters, priceRange: { min: 0, max: 0 } }),
    })
  }

  const getDefaultPhoto = (photos: Array<{ id: string; url: string; isDefault?: boolean }> | undefined) => {
    if (!photos || photos.length === 0) return ''
    return photos.find((p) => p.isDefault)?.url || photos[0].url
  }

  const itemCards = items.map((item) => ({
    id: item.id,
    title: item.title,
    image: getDefaultPhoto(item.photos),
    dailyPrice: item.dailyPrice ?? undefined,
    salePrice: item.salePrice ?? undefined,
    listingType: item.listingType,
    condition: conditionLabelMap[item.condition] || item.condition,
    location: item.addressApprox,
    ownerName: `${item.owner.firstName} ${item.owner.lastName[0]}.`,
    rating: 4.8,
    reviewCount: item._count?.rentals || 0,
    isOwn: item.owner.id === user?.id,
  }))

  const resultSummary =
    meta && !loading
      ? `Showing ${(meta.page - 1) * meta.limit + 1} – ${Math.min(meta.page * meta.limit, meta.total)} of ${meta.total} item${meta.total === 1 ? '' : 's'}`
      : null

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />

      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="bg-white border-b border-[var(--border)] py-4">
          <div className="container mx-auto px-4">
            <SearchBar onSearch={handleSearch} value={searchQuery} placeholder="Search drills, ladders, trailers..." loading={loading && items.length === 0} />
          </div>
        </div>

        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-primary)]">Browse Items</h1>
              {resultSummary && <p className="text-sm text-[var(--text-secondary)] mt-1">{resultSummary}</p>}
            </div>
            <div className={['lg:hidden', showFilters ? 'bg-[var(--bg-tint)]' : ''].join(' ')}>
              <button
                type="button"
                className="btn btn-outline text-sm flex items-center gap-2"
                onClick={() => setShowFilters(!showFilters)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {showFilters ? 'Hide Filters' : 'Filters'}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-xl mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="font-semibold">Couldn’t load items</p>
                <p className="text-sm">{error.message}</p>
              </div>
              {error.canRetry && (
                <button
                  onClick={() => setSearchParams(new URLSearchParams(searchParams), { replace: true })}
                  className="btn btn-primary shrink-0"
                >
                  Try again
                </button>
              )}
            </div>
          )}

          {activeFilterChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  onClick={chip.onRemove}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--bg-tint)] text-[var(--forest-green)] text-sm rounded-full hover:bg-[var(--forest-green)] hover:text-white transition-colors"
                >
                  {chip.label}
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              ))}
              <button
                onClick={clearFilters}
                className="text-sm text-[var(--slate-light)] hover:text-[var(--forest-green)] underline"
              >
                Clear all
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 pb-10">
            <div className={`lg:block ${showFilters ? 'block' : 'hidden'}`}>
              <FilterSidebar initialFilters={filters} onFiltersChange={handleFiltersChange} />
            </div>

            <div className="lg:col-span-3">
              {loading && items.length === 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {Array.from({ length: DEFAULT_LIMIT }).map((_, i) => (
                    <ItemCardSkeleton key={i} />
                  ))}
                </div>
              ) : itemCards.length === 0 ? (
                <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-[var(--bg-tint)] flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-[var(--forest-green)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                    {searchQuery || hasActiveFilters ? 'No matching items' : 'No items available nearby'}
                  </h3>
                  <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
                    {searchQuery || hasActiveFilters
                      ? 'Try adjusting your search or clearing your filters to see more results.'
                      : 'Be the first to list an item in your area and start earning from items you already own.'}
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    {searchQuery || hasActiveFilters ? (
                      <button onClick={clearFilters} className="btn btn-primary">
                        Clear all filters
                      </button>
                    ) : (
                      <Link to="/list-item" className="btn btn-primary">
                        List an Item
                      </Link>
                    )}
                    <Link to="/" className="btn btn-outline">
                      Go home
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {itemCards.map((item) => (
                      <ItemCard key={item.id} {...item} />
                    ))}
                  </div>

                  {meta && meta.totalPages > 1 && (
                    <div className="sticky bottom-[var(--mobile-nav-offset,0px)] z-20 flex items-center justify-between gap-3 mt-8 pt-3 bg-[var(--bg-primary)] border-t border-[var(--border)] pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]" style={{ marginBottom: '-2.5rem' }}>
                      <button
                        onClick={() => goToPage(meta.page - 1)}
                        disabled={meta.page <= 1}
                        className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-[var(--text-secondary)]">
                        Page {meta.page} of {meta.totalPages}
                      </span>
                      <button
                        onClick={() => goToPage(meta.page + 1)}
                        disabled={meta.page >= meta.totalPages}
                        className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
